/* eslint-disable consistent-return */
import { getToken } from 'firebase/app-check';
import { onAuthStateChanged, signInWithCustomToken, signOut } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import React, {
  createContext, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useCookies } from 'react-cookie';
import {
  appCheck, auth, db, fetchWatchList,
} from '../../.services/membership.services';

const defaultContext = {
  currentUser: null,
};

const AuthMembershipContext = createContext(defaultContext);
const useAuth = () => useContext(AuthMembershipContext);

function AuthMembershipProvider({ children } : any) {
  const [currentUser, setCurrentUser] = useState<any | null>(null);
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [currentUserClaims, setCurrentUserClaims] = useState<any | null>();
  const [unsubListenerClaims, setUnsubListenerClaims] = useState<any | null>(null);
  const [cookies] = useCookies(['csrf']);
  const [watchList, setWatchList] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialAuthDone = useRef(false);
  const refreshTime = useRef(null);

  const checkSession = async () => {
    const csrfToken = cookies?.csrf;
    if (!csrfToken && authenticated) {
      return signOut(auth);
    }

    if (!csrfToken) {
      return;
    }

    const headers = {
      'Content-Type': 'application/json',
    };

    if (process.env.GATSBY_SKIP_APPCHECK === 'false') {
      const appCheckTokenResponse = await getToken(appCheck);
      headers['x-firebase-appcheck-token'] = appCheckTokenResponse?.token;
    }

    try {
      const isProduction = process.env.NODE_ENV === 'production';
      const authPath = `${isProduction ? process.env.GATSBY_CHD_DASHBOARD_URL : ''}/api/auth/status`;
      const response = await fetch(authPath!, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ csrfToken }),
      });

      if (!response.ok) {
        signOut(auth);
        if (unsubListenerClaims) {
          unsubListenerClaims();
          setUnsubListenerClaims(null);
        }
        setCurrentUserClaims(null);
        setCurrentUser(null);
        setAuthenticated(false);
        throw new Error('Failed to retrieve custom token');
      }
      const { customToken } = await response.json();
      await signInWithCustomToken(auth, customToken);
      setAuthenticated(true);
    } catch (error) {
      setCurrentUser(null);
      setAuthenticated(false);
      setWatchList({});
      signOut(auth);
    }
  };

  useEffect(() => {
    if (currentUser && !cookies.csrf) {
      signOut(auth);
    }
    if (!currentUser && cookies.csrf) {
      checkSession();
      setLoading(false);
    }
  }, [cookies]);

  useEffect(() => {
    checkSession();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        setAuthenticated(true);
        const watchLaterList = await fetchWatchList();
        setWatchList(watchLaterList || {});
      } else {
        setCurrentUser(null);
        setCurrentUserClaims(null);
        setAuthenticated(false);
        setWatchList({});
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    if (!currentUser) return;

    // Get user's Custom Claims
    currentUser.getIdTokenResult().then((idTokenResult: any) => {
      setCurrentUserClaims(idTokenResult?.claims.membershipTier || null);
    }).catch((error: any) => {
      console.error(error);
    });
    // Listen to Firestore for Custom Claims updates
    const unsubscribe = onSnapshot(doc(db, 'members', auth.currentUser?.uid), async (docSnap: any) => {
      const user = auth.currentUser;
      const data = docSnap.data();
      if (user
          && initialAuthDone.current
          && data?.refreshTime
          && data.refreshTime !== refreshTime.current) {
        try {
          await user.getIdToken(true);
          const token = await user.getIdTokenResult();
          setCurrentUserClaims(token.claims?.membershipTier || null);
        } catch (error:any) {
          console.log(error);
        }
        refreshTime.current = data.refreshTime;
      }
      if (!initialAuthDone.current) {
        initialAuthDone.current = true;
      }
    });
    setUnsubListenerClaims(() => unsubscribe);

    return () => unsubscribe();
  }, [currentUser]);

  const value = useMemo(
    () => (
      {
        authenticated,
        currentUser,
        currentUserClaims,
        loading,
        setWatchList,
        watchList,
      }
    ),
    [currentUser, currentUserClaims, watchList, loading, authenticated],
  );

  return (
    <AuthMembershipContext.Provider value={value}>
      {children}
    </AuthMembershipContext.Provider>
  );
}

export function useAuthValue() {
  return useContext(AuthMembershipContext);
}

export { AuthMembershipProvider, useAuth };
